<template>
    <Breadcrumb :items="breadcrumbs" />
    <div class="d-flex justify-content-end mb-3">
        <router-link :to="absenceReportRoute" class="btn PrimaryButton">
            <i class="pi pi-chart-bar me-1" aria-hidden="true"></i> تقرير الغياب
        </router-link>
    </div>
    <Loading v-if="loading" />

    <!-- Barcode Scanner panel -->
    <div class="card mb-3 border-primary" v-if="!loading">
        <div class="card-body py-3">
            <h6 class="card-title mb-3">
                <i class="pi pi-barcode me-1"></i> تسجيل الحضور بالباركود
            </h6>
            <div class="form-group mb-3" style="max-width: 220px;">
                <label for="attendance-scan-date">تاريخ تسجيل الحضور</label>
                <input id="attendance-scan-date" v-model="scanDate" type="date" class="form-control" :disabled="isProcessingScan">
                <small class="d-block text-muted mt-1">الافتراضي هو تاريخ اليوم. غيّره لتسجيل الحضور بتاريخ آخر.</small>
            </div>
            <BarcodeScanner mode="attendance" :processing="isProcessingScan"
                @scanned="handleAttendanceQR" @scan-error="handleAttendanceScanError" />
            <!-- Reserve feedback space so scan progress cannot shift the viewport. -->
            <div :class="{ invisible: !scanLookupLoading }" class="mt-2 text-primary small">
                <span class="spinner-border spinner-border-sm me-1"></span>
                جاري تسجيل الحضور...
            </div>
        </div>
    </div>

    <div class="row mx-0" v-if="!loading">
        <div class="col-lg-3 col-md-6 col-12">
            <div class="form-group d-flex flex-column">
                <label>المجموعة</label>
                <Dropdown v-model="selectedGroup" :options="groups" showClear optionLabel="name" placeholder="كل الطلاب (بدون مجموعة)"
                    @change="onGroupChange" />
            </div>
        </div>
        <div class="col-lg-3 col-md-6 col-12" v-if="!selectedGroup">
            <div class="form-group">
                <label for="search">ابحث</label>
                <input type="search" id="search" class="form-control" placeholder="ابحث" v-model="word"
                    @keyup="debounceFetchAttendance">
            </div>
        </div>
        <div class="col-lg-2 col-md-6 col-12">
            <div class="form-group d-flex flex-column">
                <label>الشهور</label>
                <Dropdown v-model="selectedMonth" :options="months" showClear optionLabel="name" placeholder="الشهور"
                    @change="fetchAttendance" />
            </div>
        </div>
        <div class="col-lg-2 col-md-6 col-12" v-if="!selectedGroup">
            <div class="form-group d-flex flex-column">
                <label>الجنس</label>
                <Dropdown v-model="gender" :options="gender_list" showClear optionLabel="title" placeholder="الجنس"
                    @change="fetchAttendance" />
            </div>
        </div>
        <div class="col-lg-2 col-md-3 col-12">
            <div class="d-flex">
                <div class="form-group d-flex flex-column mx-1">
                    <label class="non_opacity">Excel</label>
                    <button class="btn btn-success px-3" @click="exportToExcel">
                        <i class="pi pi-file-excel"></i>
                    </button>
                </div>
                <div class="form-group d-flex flex-column mx-1">
                    <label class="non_opacity">طباعة</label>
                    <button class="btn btn-danger px-3" v-print="'#reservationTable'">
                        <i class="pi pi-file-pdf"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Bulk absence notify row -->
    <div class="row mx-0 mb-3 mt-2" v-if="!loading">
        <div class="col-12">
            <div class="d-flex align-items-center gap-2 flex-wrap">
                <label class="mb-0 fw-semibold">إشعارات الغياب ليوم:</label>
                <input type="date" class="form-control" style="max-width:180px" v-model="bulkNotifyDate" />
                <button class="btn btn-warning" @click="sendBulkAbsentNotify" :disabled="bulkNotifying">
                    <i class="pi pi-bell me-1"></i>
                    {{ bulkNotifying ? 'جاري الإرسال...' : 'إرسال إشعارات الغياب' }}
                </button>
            </div>
        </div>
    </div>

    <p class="text-muted small mb-2" v-if="!loading && selectedGroup && !scheduledDates.length">
        لا توجد أيام حضور مجدولة لهذه المجموعة في هذا الشهر.
    </p>

    <div class="table-responsive attendance-table" v-if="!loading" id="reservationTable">
        <table class="table table-bordered">
            <thead>
                <tr>
                    <th>الاسم</th>
                    <th class="text-center">حضور الشهر</th>
                    <th class="text-center">غياب الشهر</th>
                    <th class="text-center">الأيام المقررة للحضور</th>
                    <th class="text-center" v-if="selectedGroup">إجمالي الحضور</th>
                    <th class="text-center" v-if="selectedGroup">إجمالي الغياب</th>
                    <th v-for="day in scheduledDates" :key="day">
                        <p class="mb-0">{{ getDayName(day) }}</p>
                        <p class="mb-0">{{ formatDayMonth(day) }}</p>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="student in students" :key="student.user_id"
                    :data-student-row="student.user_id"
                    :class="{ 'row-highlight': highlightedUserId === student.user_id }">
                    <td>{{ student.name }}</td>
                    <td class="text-center fw-bold text-success">{{ student.present_month }}</td>
                    <td class="text-center fw-bold text-danger">{{ student.absent_month }}</td>
                    <td class="text-center text-muted">{{ student.scheduled_month }}</td>
                    <td class="text-center" v-if="selectedGroup">{{ student.present_total }}</td>
                    <td class="text-center" v-if="selectedGroup">{{ student.absent_total }}</td>
                    <td v-for="d in student.dates" :key="d.date" class="text-center att-cell"
                        :class="[d.present ? 'att-present' : '', d.scheduled === false ? 'att-na' : '']"
                        :title="d.scheduled === false ? 'خارج جدول مجموعة الطالب — يمكن تسجيل الحضور يدويًا' : ''"
                        @click="toggleAttendance(student.user_id, d.date)"
                        >
                        <span v-if="d.scheduled === false && !d.present" class="text-muted">—</span>
                        <i v-else-if="d.present" class="pi pi-check att-icon-present"></i>
                        <i v-else class="pi pi-times att-icon-absent"></i>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>

    <!-- Absence notification dialog -->
    <Dialog v-model:visible="visible" modal header="رسالة الغياب" :style="{ width: '50vw' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
        <Textarea v-model="attendance_message" style="display: block; width: 100%;" cols="30" rows="5" />
        <div class="text-end mt-1" :class="attendance_message.length > 70 ? 'text-danger' : 'text-success'">
            {{ attendance_message.length }} حرف
            <span class="ms-2">عدد الرسائل: {{ smsCount }}</span>
        </div>
        <button type="button" class="PrimaryButton mt-3" @click="SendAttendanceMessage">إرسال</button>
    </Dialog>

    <!-- Absence bulk-notify preview/confirmation -->
    <NotificationPreviewDialog
        v-model:visible="absencePreview.visible"
        title="معاينة إشعار الغياب"
        :loading="absencePreview.loading"
        :sending="absencePreview.sending"
        :blocked-message="absencePreview.blockedMessage"
        :whatsapp-available="absencePreview.whatsappAvailable"
        :summary="absencePreview.summary"
        :recipients="absencePreview.recipients"
        :no-phone="absencePreview.noPhone"
        @cancel="absencePreview.visible = false"
        @confirm="confirmSendAbsentNotify"
    />
</template>

<script>
import axios from "axios";
import Dropdown from 'primevue/dropdown';
import Dialog from 'primevue/dialog';
import Swal from 'sweetalert2';
import Textarea from 'primevue/textarea';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import BarcodeScanner from '@/components/global/BarcodeScanner.vue';
import NotificationPreviewDialog from '@/components/notifications/NotificationPreviewDialog.vue';
import * as XLSX from 'xlsx/xlsx.mjs';
import { useGradeStore } from '@/stores/useGradeStore.js';
import { useNotificationStore } from '@/stores/useNotificationStore.js';

export default {
    components: { Dropdown, Dialog, Textarea, Breadcrumb, Loading, BarcodeScanner, NotificationPreviewDialog },

    data() {
        return {
            students: [],
            scheduledDates: [],
            groups: [],
            selectedGroup: null,
            selectedMonth: {
                id: new Date().getMonth() + 1,
                name: new Date().toLocaleString('default', { month: 'long' }),
            },
            months: [
                { id: 1,  name: 'يناير'  }, { id: 2,  name: 'فبراير' },
                { id: 3,  name: 'مارس'   }, { id: 4,  name: 'أبريل'  },
                { id: 5,  name: 'مايو'   }, { id: 6,  name: 'يونيو'  },
                { id: 7,  name: 'يوليو'  }, { id: 8,  name: 'أغسطس' },
                { id: 9,  name: 'سبتمبر' }, { id: 10, name: 'أكتوبر' },
                { id: 11, name: 'نوفمبر' }, { id: 12, name: 'ديسمبر' },
            ],
            gender_list: [
                { id: 1, title: 'ولد' },
                { id: 2, title: 'بنت' },
            ],
            word: '',
            gender: null,
            searchTimeout: null,

            // QR scan lookup
            scanLookupLoading: false,
            isProcessingScan: false,
            highlightedUserId: null,
            // Attendance scanning date — defaults to today on every page
            // load/reload; only changes when the admin explicitly picks
            // another date. Sent as-is to POST /attendance/qr on every scan.
            scanDate: new Date().toISOString().slice(0, 10),

            // Individual notification dialog
            visible: false,
            attendance_message: '',
            parent_phone: null,
            attendance_id: null,

            // Bulk notification
            bulkNotifyDate: new Date().toISOString().slice(0, 10),
            bulkNotifying: false,

            // Absence bulk-notify preview/confirmation dialog
            absencePreview: {
                visible: false,
                loading: false,
                sending: false,
                blockedMessage: null,
                whatsappAvailable: true,
                summary: {},
                recipients: [],
                noPhone: [],
            },

            // Templates cache (code → template object)
            templates: {},

            loading: false,
        };
    },

    computed: {
        absenceReportRoute() {
            return {
                name: 'gradeAttendanceAbsenceReport',
                params: { gradeSlug: this.gradeSlug },
                query: {
                    month: new Date().getFullYear() + '-' + String(this.selectedMonth?.id ?? new Date().getMonth() + 1).padStart(2, '0'),
                    word: this.word || undefined,
                    gender: this.gender?.id || undefined,
                    group_id: this.selectedGroup?.id || undefined,
                },
            };
        },
        smsCount() {
            return Math.ceil(this.attendance_message.length / 70) || 1;
        },
        gradeStore() { return useGradeStore(); },
        notificationStore() { return useNotificationStore(); },
        gradeSlug() { return this.$route.params.gradeSlug; },
        grade() {
            const slug = this.gradeSlug;
            if (!slug) return null;
            return this.gradeStore.activeGrades.find(g => g.slug === slug)
                || this.gradeStore.allGrades.find(g => g.slug === slug)
                || null;
        },
        gradeId() { return this.grade?.id ?? null; },
        breadcrumbs() {
            const gradeName = this.grade?.name ?? this.gradeSlug ?? '';
            const gradePath = this.grade ? `/grades/${this.grade.slug}/students` : '/';
            return [
                { name: 'الرئيسية', path: '/' },
                { name: gradeName, path: gradePath },
                { name: 'الحضور', path: this.$route.fullPath },
            ];
        },
    },

    methods: {
        debounceFetchAttendance(event) {
            clearTimeout(this.searchTimeout);

            // Keyboard-wedge scanners type into whichever input has focus.
            // While a scan action is active, never let those keyups schedule the
            // independent manual listing request. Enter marks the end of a scan.
            if (this.isProcessingScan) {
                if (event?.key === 'Enter') this.removeScannedSearchValue();
                return;
            }

            this.searchTimeout = setTimeout(() => this.fetchAttendance(), 1500);
        },

        removeScannedSearchValue(raw = null) {
            if (raw) {
                while (this.word.endsWith(raw)) this.word = this.word.slice(0, -raw.length);
                return;
            }

            const httpIndex = Math.max(this.word.lastIndexOf('https://'), this.word.lastIndexOf('http://'));
            if (httpIndex >= 0 && this.word.slice(httpIndex).includes('/qr/')) {
                this.word = this.word.slice(0, httpIndex);
            }
        },

        handleAttendanceScanError({ message, raw } = {}) {
            clearTimeout(this.searchTimeout);
            this.removeScannedSearchValue(raw);
            this.notificationStore.push({
                icon: 'error',
                title: message || 'QR غير صالح',
                timeout: 5000,
            });
        },

        getDayName(dateStr) {
            const days = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
            return days[new Date(dateStr).getDay()];
        },

        formatDayMonth(dateStr) {
            const [, mm, dd] = dateStr.split('-');
            return `${dd}-${mm}`;
        },

        getDaysOfMonth(year, month) {
            const days = [];
            const date = new Date(year, month, 1);
            while (date.getMonth() === month) {
                const yyyy = date.getFullYear();
                const mm   = String(date.getMonth() + 1).padStart(2, '0');
                const dd   = String(date.getDate()).padStart(2, '0');
                days.push(`${yyyy}-${mm}-${dd}`);
                date.setDate(date.getDate() + 1);
            }
            return days;
        },

        async fetchGroups() {
            if (!this.gradeId) { this.groups = []; return; }
            try {
                const { data } = await axios.get(`/academic-years/${this.gradeId}/groups`);
                this.groups = data.data ?? [];
            } catch { this.groups = []; }
        },

        onGroupChange() {
            // A group's roster is exact and self-contained — the free-text/gender
            // filters (which only apply to the whole-grade legacy view) no longer
            // mean anything once a group is selected.
            this.word = '';
            this.gender = null;
            this.fetchAttendance();
        },

        monthParam() {
            // selectedMonth only carries a month number (no year) — matches the
            // page's existing (pre-existing, unrelated) always-current-year
            // assumption; not changed here.
            const year = new Date().getFullYear();
            return `${year}-${String(this.selectedMonth.id).padStart(2, '0')}`;
        },

        // Single entry point — branches to the group-schedule-aware grid or the
        // legacy whole-grade/all-calendar-days view depending on the group filter.
        async fetchAttendance() {
            if (!this.selectedMonth?.id) {
                this.selectedMonth = { id: new Date().getMonth() + 1, name: '' };
            }

            if (this.selectedGroup) {
                await this.fetchGroupAttendance();
            } else {
                await this.fetchLegacyAttendance();
            }
        },

        // Group-schedule-aware grid: scheduled dates + present/absent + monthly
        // and all-time totals, all computed server-side in one request
        // (StudentGroupController::attendance) — never one request per student.
        async fetchGroupAttendance() {
            try {
                const { data } = await axios.get(`/groups/${this.selectedGroup.id}/attendance`, {
                    params: { month: this.monthParam() },
                });
                const payload = data.data ?? {};
                this.scheduledDates = payload.scheduled_dates ?? [];
                this.students = (payload.students ?? []).map(s => ({
                    user_id: s.user_id,
                    name: s.name,
                    dates: s.dates ?? [],
                    present_month: s.present_month ?? 0,
                    absent_month: s.absent_month ?? 0,
                    scheduled_month: s.scheduled_month ?? (payload.scheduled_dates ?? []).length,
                    present_total: s.present_total ?? 0,
                    absent_total: s.absent_total ?? 0,
                }));
            } catch {
                this.scheduledDates = [];
                this.students = [];
            } finally {
                this.loading = false;
            }
        },

        // Legacy view ("كل المجموعات" — no group filter): every eligible
        // (non-online — enforced server-side) student in the grade, EACH
        // evaluated against THEIR OWN group's schedule and their academic
        // year's attendance_start_date — never one shared/global schedule
        // for everyone. Server-computed in a single request
        // (StudentGroupController::attendanceOverview /
        // StudentGroupService::attendanceOverviewForYear), reusing the exact
        // same AttendanceScheduleService the single-group view uses.
        //
        // The rendered column set is still every calendar day of the month
        // (unchanged UI/layout) — each student's own `dates` entries instead
        // carry a `scheduled` flag so a day outside THEIR schedule renders as
        // "not applicable" rather than a false absence, without the column
        // count ever differing between rows.
        async fetchLegacyAttendance() {
            if (!this.gradeId) { this.scheduledDates = []; this.students = []; this.loading = false; return; }
            try {
                const { data } = await axios.get(`/academic-years/${this.gradeId}/attendance`, {
                    params: {
                        month: this.monthParam(),
                        word: this.word || undefined,
                        gender: this.gender?.id || undefined,
                    },
                });
                const payload = data.data ?? {};
                this.scheduledDates = payload.scheduled_dates ?? [];
                this.students = (payload.students ?? []).map(s => ({
                    user_id: s.user_id,
                    name: s.name,
                    phone: s.phone,
                    gender: s.gender,
                    dates: s.dates ?? [],
                    present_month: s.present_month ?? 0,
                    absent_month: s.absent_month ?? 0,
                    // Per-student — computed from THEIR OWN group's schedule,
                    // never the shared column count (see attendanceOverviewForYear()).
                    scheduled_month: s.scheduled_month ?? 0,
                    // No single group filter here — an all-time total has no
                    // single schedule to anchor to across a mixed set of groups.
                    present_total: null,
                    absent_total:  null,
                }));
            } catch {
                this.scheduledDates = [];
                this.students = [];
            } finally {
                this.loading = false;
            }
        },

        async loadTemplates() {
            try {
                const res = await axios.get('/whatsapp-templates');
                const list = res.data.data ?? [];
                this.templates = Object.fromEntries(list.map(t => [t.code, t]));
            } catch { /* non-fatal */ }
        },

        buildAbsenceMessage(student, date) {
            const tpl = this.templates['absence_notification'];
            if (tpl?.is_active) {
                return tpl.message
                    .replace(/\{\{student_name\}\}/g, student.name)
                    .replace(/\{\{date\}\}/g, date)
                    .replace(/\{\{teacher_name\}\}/g, 'أ.طارق عبدالغني عبدالباري')
                    .replace(/\{\{academic_year\}\}/g, this.grade?.name ?? '');
            }
            const pronoun = student.gender == 1 ? 'أبنكم' : 'أبنتكم';
            return `أ.طارق عبدالغني عبدالباري مدرس الرياضيات\nيحيط علم سيادتكم بأن ${pronoun} (${student.name}) لم يحضر يوم ${this.getDayName(date)} الموافق ${this.formatDayMonth(date)}`;
        },

        async handleAttendanceQR({ raw, qrToken, studentId, resolvedStudent }) {
            console.log('[Attendance] Callback fired', { raw, qrToken, studentId });

            const resolvedUserId = resolvedStudent?.user_id ?? studentId;
            if (this.isProcessingScan) return;

            // A hardware scanner may type into the focused manual search input.
            // Cancel that pending manual debounce and remove only the scanned
            // suffix so it cannot trigger the independent listing flow later.
            clearTimeout(this.searchTimeout);
            this.removeScannedSearchValue(raw);

            this.isProcessingScan  = true;
            this.scanLookupLoading = true;

            try {
                // Single-request flow: for a QR scan, BarcodeScanner (attendance
                // mode) no longer resolves the token itself via GET
                // /qr/resolve/{token} — it hands the raw token straight through,
                // and POST /attendance/qr resolves it AND registers attendance
                // in the SAME request. One physical scan = one API call.
                if (qrToken) {
                    await this.markAttendanceFromScan({ qrToken });
                    return;
                }

                let student = null;

                if (resolvedUserId != null) {
                    // A local STU-/numeric id is the authoritative lookup — no
                    // extra request needed. The visible row is optional and
                    // only enriches the local display name.
                    const visibleStudent = this.students.find(
                        (row) => String(row.user_id) === String(resolvedUserId)
                    );
                    student = {
                        user_id: Number(resolvedUserId),
                        name: resolvedStudent?.name ?? visibleStudent?.name ?? `#${resolvedUserId}`,
                    };
                } else if (raw) {
                    // No locally/QR-resolved id — BarcodeScanner has already
                    // rejected anything that doesn't look like plausible ASCII
                    // scanner text, so `raw` here is a genuine free-text value
                    // (e.g. a reservation code) worth actually asking the
                    // backend about. "لم يتم العثور على الطالب" below is only
                    // ever shown AFTER this real lookup confirms zero matches —
                    // never before a request was made.
                    const matches = await this.searchStudentsForScan(raw);

                    if (!matches.length) {
                        this.notificationStore.push({
                            icon: 'error',
                            title: 'لم يتم العثور على الطالب',
                            timeout: 5000,
                        });
                        return;
                    }

                    student = matches.length === 1
                        ? matches[0]
                        : await this.chooseFromMultipleMatches(matches);
                    if (!student) return; // user cancelled the multi-match picker
                } else {
                    // Nothing to look up at all (BarcodeScanner already emits
                    // scan-error instead of this for invalid input; this is a
                    // defensive fallback, not the normal path).
                    this.notificationStore.push({
                        icon: 'error',
                        title: 'قيمة الماسح غير صالحة — أعد المحاولة',
                        timeout: 5000,
                    });
                    return;
                }

                console.log('[Attendance] Lookup success:', student);
                await this.markAttendanceFromScan({ student });
            } catch (err) {
                console.log('[Attendance] Scan action failed:', err);
                this.notificationStore.push({
                    icon: 'error',
                    title: err.response?.data?.message ?? 'تعذّر تسجيل الحضور',
                    timeout: 5000,
                });
            } finally {
                this.scanLookupLoading = false;
                this.isProcessingScan  = false;
            }
        },

        // Looks up students matching a scanned free-text value (reservation
        // code, etc.) within the current grade, via the same search endpoint
        // fetchAttendance() uses. `show_all` because there must be no
        // possibility of a real match being truncated out of a small default
        // page just for a scan lookup.
        async searchStudentsForScan(term) {
            const formData = new FormData();
            if (this.gradeId) formData.append('academic_year_id', this.gradeId);
            formData.append('status', 1);
            formData.append('word', term);
            formData.append('show_all', 1);

            const res = await axios.post('/all_reservations', formData);
            const rows = res.data.data?.data ?? [];
            return rows.map((r) => ({ user_id: r.user_id, name: r.name, gender: r.gender }));
        },

        async chooseFromMultipleMatches(matches) {
            const inputOptions = Object.fromEntries(
                matches.map((s) => [s.user_id, s.name])
            );
            const { value: chosenId } = await Swal.fire({
                icon: 'question',
                title: 'تم العثور على أكثر من طالب',
                input: 'radio',
                inputOptions,
                confirmButtonText: 'اختيار',
                cancelButtonText: 'إلغاء',
                showCancelButton: true,
            });
            if (!chosenId) return null;
            return matches.find((s) => String(s.user_id) === String(chosenId)) ?? null;
        },

        // Marks attendance for EITHER an already-resolved student (POSTs
        // {user_id} — the STU-/numeric-id and free-text-search paths) OR a raw
        // QR token (POSTs {qr_token} — the single-request scan flow: the
        // backend resolves the token AND registers attendance in this SAME
        // call, via the centralized QrTokenResolverService, so a QR scan is
        // exactly one API request). Response handling is identical either way
        // since the backend returns the same shape for both.
        //
        // Result codes: ATTENDANCE_RECORDED / ALREADY_RECORDED = normal,
        // successful outcomes, never an error. NOT_ELIGIBLE = a legitimate,
        // expected outcome (this student's group doesn't meet today), also
        // never an error. INVALID_QR / STUDENT_NOT_FOUND = the scan genuinely
        // could not be resolved server-side.
        async markAttendanceFromScan({ student = null, qrToken = null }) {
            const timeStr = new Date().toTimeString().slice(0, 8);
            const payload = qrToken ? { qr_token: qrToken } : { user_id: student.user_id ?? student.id };
            // The scanning page's date picker (defaults to today) — sent
            // explicitly so the backend never has to guess/assume "now" when
            // the admin picked a different date.
            if (this.scanDate) payload.date = this.scanDate;

            try {
                const res = await axios.post('/attendance/qr', payload);
                const userId = res.data.user_id ?? student?.user_id ?? student?.id ?? null;
                const studentName = res.data.student_name || student?.name || '';
                // Always the date actually recorded by the backend — the
                // request's own scanDate, confirmed back — never the
                // client's own "now", which would be wrong for a past date.
                const recordedDate = res.data.date
                    ? new Date(`${res.data.date}T00:00:00`).toLocaleDateString('ar-EG', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
                    : '';
                console.log('[Attendance] Page action executed:', res.data.code, 'for', userId);

                if (res.data.code === 'INVALID_QR' || res.data.code === 'STUDENT_NOT_FOUND') {
                    this.notificationStore.push({
                        icon:  'error',
                        title: res.data.message || 'رمز QR غير صالح',
                        timeout: 5000,
                    });
                    return;
                }

                if (res.data.code === 'NOT_ELIGIBLE') {
                    // Not present, not absent — this student's group simply
                    // doesn't meet on the selected date. A normal, expected
                    // outcome, never an error.
                    this.notificationStore.push({
                        icon:  'info',
                        title: res.data.message || 'التاريخ المحدد ليس من أيام حضور المجموعة',
                        text:  studentName,
                        timeout: 5000,
                    });
                    if (userId != null) await this.revealStudentRow(userId);
                    return;
                }

                if (res.data.code === 'ALREADY_RECORDED') {
                    this.notificationStore.push({
                        icon:  'warning',
                        title: res.data.message || 'تم تسجيل حضور الطالب مسبقًا',
                        text:  studentName,
                        timeout: 5000,
                    });
                } else {
                    this.notificationStore.push({
                        icon:  'success',
                        title: 'تم تسجيل حضور الطالب بنجاح',
                        text:  `${studentName} — ${recordedDate} ${timeStr}`,
                        timeout: 5000,
                    });
                }

                if (userId != null) {
                    // Update only an already-rendered row. Off-page/filtered
                    // students still succeed without reloading the attendance list.
                    this.applyLocalAttendance(userId, res.data.data?.date);
                    await this.revealStudentRow(userId);
                }
            } catch (err) {
                this.notificationStore.push({
                    icon: 'error',
                    title: err.response?.data?.message ?? 'تعذّر تسجيل الحضور',
                    timeout: 5000,
                });
            }
        },

        // Reflects a just-recorded date on the already-loaded row without a network
        // round-trip — avoids re-fetching every student's attendance after each scan.
        // A no-op if the date isn't one of the currently displayed scheduled
        // columns (different month on screen, or — group mode — the date simply
        // isn't a scheduled day for that group, which the backend would have
        // already rejected as NOT_SCHEDULED_DAY before this is ever called).
        applyLocalAttendance(userId, dateStr) {
            if (!dateStr) return;
            const student = this.students.find(s => s.user_id === userId);
            if (!student) return;

            const entry = student.dates.find(d => d.date === dateStr);
            if (!entry || entry.present) return;

            entry.present = true;
            student.present_month = (student.present_month ?? 0) + 1;
            student.absent_month = Math.max((student.absent_month ?? 0) - 1, 0);
            if (student.present_total != null) student.present_total += 1;
            if (student.absent_total != null) student.absent_total = Math.max(student.absent_total - 1, 0);
        },

        // Highlight only an already-rendered row without moving the viewport.
        // Off-page/filtered students still record without triggering the list loader.
        async revealStudentRow(userId) {
            const alreadyVisible = this.students.some((s) => s.user_id === userId);
            if (!alreadyVisible) return;

            await this.$nextTick();

            this.highlightedUserId = userId;
            setTimeout(() => {
                if (this.highlightedUserId === userId) this.highlightedUserId = null;
            }, 2000);
        },

        async toggleAttendance(user_id, date) {
            const res = await axios.post('/attendance', { user_id, date })
                .catch(err => { console.error(err); return null; });

            if (!res) return;

            this.parent_phone  = res.data.parent_phone;
            this.attendance_id = res.data.attendance?.id ?? Date.now();

            await this.fetchAttendance();
        },

        // Params shared by BOTH the preview call and the actual send call —
        // whatever filters are active on screen right now (group/search),
        // exactly like the backend resolver requires for the two to match.
        absenceNotifyParams() {
            return {
                academic_year_id: this.gradeId,
                date:             this.bulkNotifyDate,
                group_id:         this.selectedGroup?.id || undefined,
                word:             (!this.selectedGroup && this.word) ? this.word : undefined,
            };
        },

        // Step 1: load the recipient preview — never sends anything itself.
        async sendBulkAbsentNotify() {
            if (!this.gradeId) {
                Swal.fire({ icon: 'warning', title: 'تنبيه', text: 'لم يتم تحديد الفصل', timer: 2000, showConfirmButton: false });
                return;
            }

            this.bulkNotifying = true;
            this.absencePreview.visible = true;
            this.absencePreview.loading = true;
            this.absencePreview.blockedMessage = null;
            try {
                const { data } = await axios.post('/attendance/notifications/preview', this.absenceNotifyParams());
                this.absencePreview.summary = data.summary;
                this.absencePreview.recipients = data.recipients;
                this.absencePreview.noPhone = data.no_phone;
                this.absencePreview.whatsappAvailable = data.whatsapp_available;
                this.absencePreview.blockedMessage = data.blocked_by_start_date
                    ? 'تاريخ الإرسال المحدد قبل تاريخ بداية احتساب الغياب لهذه السنة الدراسية.'
                    : null;
            } catch {
                this.absencePreview.visible = false;
                Swal.fire({ icon: 'error', title: 'خطأ', text: 'تعذّر تحميل قائمة المستلمين', timer: 3000, showConfirmButton: false });
            } finally {
                this.absencePreview.loading = false;
                this.bulkNotifying = false;
            }
        },

        // Step 2: admin confirmed from the preview dialog — actual send.
        // Re-sends the SAME filters (never a list of student ids) so the
        // backend recomputes eligibility itself right before sending.
        async confirmSendAbsentNotify() {
            this.absencePreview.sending = true;
            try {
                const res = await axios.post('/attendance/bulk-notify-absent', this.absenceNotifyParams());
                const { total_recipients, queued_chunks, failed_chunks, channel } = res.data;
                const channelLabel = channel === 'sms' ? 'SMS (واتساب غير متصل)' : 'واتساب';
                this.absencePreview.visible = false;
                Swal.fire({
                    icon:  queued_chunks > 0 ? 'success' : 'info',
                    title: 'اكتمل الإرسال',
                    html:  `القناة: <strong>${channelLabel}</strong><br>عدد المستلمين: <strong>${total_recipients}</strong><br>دفعات تم إرسالها: <strong>${queued_chunks}</strong><br>دفعات فشلت: <strong>${failed_chunks}</strong>`,
                    timer: 6000,
                    showConfirmButton: true,
                });
            } catch {
                Swal.fire({ icon: 'error', title: 'خطأ', text: 'حدث خطأ أثناء الإرسال', timer: 3000, showConfirmButton: false });
            } finally {
                this.absencePreview.sending = false;
            }
        },

        async SendAttendanceMessage() {
            const phone = '2' + this.parent_phone;

            let hasWhatsApp = false;
            try {
                const r = await axios.post('/whatsapp/check', { phone });
                hasWhatsApp = r.data.has_whatsapp === true;
            } catch { hasWhatsApp = false; }

            if (hasWhatsApp) {
                try {
                    const r = await axios.post('/whatsapp/send', { phone, message: this.attendance_message });
                    if (r.data.success) {
                        Swal.fire({ icon: 'success', title: 'تم الإرسال', text: 'تم إرسال الرسالة عبر واتساب بنجاح', timer: 1500, showConfirmButton: false });
                        this.visible = false;
                        return;
                    }
                } catch { /* fall to SMS */ }
            }

            const formData = new FormData();
            formData.append('SenderName', 'SMS invite');
            formData.append('RequestID', this.attendance_id);
            formData.append('PhoneNumber', phone);
            formData.append('Message', this.attendance_message);

            await axios.post(
                'https://hubapi.advansystelecom.com/api/bulkSMS/ForwardSMS',
                formData,
                { headers: { 'Authorization': '667d0a1e-f2b3-4bd5-8569-360b6e7b80b0', 'Content-Type': 'application/json' } }
            ).then(res => {
                if (res.data === '1') {
                    Swal.fire({ icon: 'success', title: 'تم الإرسال', text: 'تم إرسال الرسالة بنجاح', timer: 1500, showConfirmButton: false });
                    this.visible = false;
                }
            }).catch(err => {
                const code = err.response?.data?.Message;
                const msgs = { '-1': 'رمز التوثيق غير صحيح', '-2': 'رقم الجوال فارغ', '-3': 'نص الرسالة فارغ', '-4': 'اسم المرسل غير صحيح', '-5': 'لا يوجد رصيد كافٍ' };
                Swal.fire({ icon: 'error', title: 'خطأ', text: msgs[code] ?? 'حدث خطأ غير متوقع', timer: 3000, showConfirmButton: false });
            });
        },

        exportToExcel() {
            const table = document.getElementById('reservationTable')?.cloneNode(true);
            if (!table) return;
            const ws = XLSX.utils.table_to_sheet(table);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'الحضور');
            XLSX.writeFile(wb, `حضور-${this.grade?.name ?? ''}.xlsx`);
        },
    },

    async mounted() {
        if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
        if (!this.gradeStore.allGrades.length)   await this.gradeStore.fetchAllGrades();
        this.loadTemplates();
        this.fetchGroups();
        this.fetchAttendance();
    },
};
</script>

<style scoped>
/* The table is the page's dedicated two-axis scroll region. Keeping the
   region below the sticky navbar prevents its header from hiding underneath
   navigation while the single table keeps header/body columns aligned. */
#reservationTable.attendance-table {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    position: sticky;
    top: var(--navbar-height, 64px);
    z-index: 1;
    overflow-x: auto;
    overflow-y: auto;
    max-height: calc(100vh - var(--navbar-height, 64px) - 1rem);
    max-height: calc(100dvh - var(--navbar-height, 64px) - 1rem);
    -webkit-overflow-scrolling: touch;
}
.attendance-table > table {
    min-width: max-content;
}
.attendance-table th,
.attendance-table td {
    white-space: nowrap;
}

/* Stick to the top of the table scroll region; the opaque fill hides rows
   underneath while the cells continue to scroll horizontally with the body. */
.attendance-table thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #f8fafc !important;
    background-image: none !important;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);
}

@media print {
    /* Root cause of the blank print/PDF: grades/students.vue ships a
       page-wide (unscoped) print rule — `body * { visibility: hidden !important; }`,
       revealed only for its OWN #printReport target — that ships on every
       route because vue3-print-nb (see the v-print directive above) builds
       its print document by copying every rule out of document.styleSheets,
       not just this component's own CSS. Without an exception for THIS
       page's print target, #reservationTable inherited visibility:hidden
       while still reserving its full layout height, which is exactly the
       reported "print dialog opens but the preview is blank" symptom. Same
       fix already applied for the same reason in payments/index.vue's
       #paymentsTable and grades/groups/show.vue's #groupStudentsExportTable
       — mirrored here, not modifying grades/students.vue's rule or any
       other page. */
    #reservationTable,
    #reservationTable * {
        visibility: visible !important;
    }

    /* Undo the screen-only two-axis scroll region: a constrained max-height
       plus overflow:auto is itself a second cause of blank/clipped print
       output — Chrome's print layout only rasterizes the currently-visible
       scrolled viewport of such a container, not its full scrollable
       content. position:sticky is also reset since it establishes its own
       containing-block quirks that print layout does not reliably resolve. */
    #reservationTable.attendance-table {
        position: static !important;
        overflow: visible !important;
        max-height: none !important;
        height: auto !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
        direction: rtl;
    }
    .attendance-table > table {
        width: 100% !important;
        min-width: 0 !important;
    }
    .attendance-table thead th {
        position: static !important;
        box-shadow: none !important;
        background-color: #fff !important;
        background-image: none !important;
    }
    .attendance-table th,
    .attendance-table td {
        white-space: normal;
    }

    /* Table header must repeat on every printed page; each data row must
       stay on one page instead of splitting across a page break. */
    #reservationTable table thead {
        display: table-header-group;
    }
    #reservationTable table tbody {
        display: table-row-group;
    }
    #reservationTable table tbody tr {
        break-inside: avoid;
        page-break-inside: avoid;
    }
}

.att-cell {
    cursor: pointer;
    min-width: 48px;
    transition: background-color 0.12s;
}
.att-cell:hover {
    background-color: rgba(212, 160, 23, 0.08) !important;
}
.att-present {
    background-color: #d4edda;
}
.att-present:hover {
    background-color: #c3e6cb !important;
}
.att-icon-present {
    color: #28a745;
    font-size: 0.95rem;
}
.att-icon-absent {
    color: #dc3545;
    opacity: 0.35;
    font-size: 0.85rem;
}
/* Day outside this student's OWN group schedule (or before their academic
   year's attendance_start_date) — not counted as scheduled/absent, but still
   clickable for a manual override (existing behavior, unchanged). */
.att-na {
    background-color: #f1f3f5;
}
.att-na:hover {
    background-color: #e9ecef !important;
}

/* ── QR scan result ── */
.row-highlight {
    animation: row-highlight-flash 2s ease-out;
}
@keyframes row-highlight-flash {
    0%, 40%   { background-color: #fff3cd; }
    100%      { background-color: transparent; }
}
</style>
